import React from "react";
import { Image, Text, TouchableOpacity, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import { AppImages } from "../../../../utility/AppImages";
import { format } from "date-fns";

interface MobileGameShowCardProps {
  details: any;
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
}

const MobileGameShowCard: React.FC<MobileGameShowCardProps> = ({
  details,
  timeLeft,
}) => {
  const eventDate = details?.eventStartTime
    ? format(new Date(details.eventStartTime), "EEEE, dd MMM, yyyy, h:mmaaa")
    : "";

  const stakes = details?.stakes || [];
  const getBeltIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "gold":
        return AppImages.icGoldBelt;
      case "silver":
        return AppImages.icSilverBelt;
      case "bronze":
        return AppImages.icBronzeBelt;
      case "platinum":
        return AppImages.icPlatinumBelt;
      case "coin":
        return AppImages.icCoinBelt;
      default:
        return AppImages.icBronzeBelt;
    }
  };

  const getBeltCount = (type: string) => {
    const stake = stakes.find(
      (s: any) => s.type.toLowerCase() === type.toLowerCase()
    );
    return stake?.quantity || 0;
  };

  // Define belt types to check
  const beltTypes = ["bronze", "silver", "gold", "platinum"];

  return (
    <View style={{ gap: 12 }}>
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.8)", "rgba(17, 24, 39, 0.9)"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          borderRadius: 12,
          borderWidth: 1,
          borderColor: "rgba(55, 65, 81, 0.5)",
          padding: 16,
          alignItems: "center",
        }}
      >
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_20,
            { color: AppColors.white, marginBottom: 12, textAlign: "center" },
          ]}
        >
          {details?.eventTitle || "Gaming Extravaganza"}
        </Text>
        {/* Countdown Timer */}
        <View
          style={{
            flexDirection: "row",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <View style={{ alignItems: "center" }}>
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 6,
                backgroundColor: "rgba(220, 38, 38, 0.40)",
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_24,
                  { color: AppColors.red_FA0A0A },
                ]}
              >
                {String(timeLeft.days).padStart(2, "0")}
              </Text>
            </View>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3 },
              ]}
            >
              Days
            </Text>
          </View>
          <View style={{ alignItems: "center" }}>
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 6,
                backgroundColor: "rgba(220, 38, 38, 0.40)",
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_24,
                  { color: AppColors.red_FA0A0A },
                ]}
              >
                {String(timeLeft.hours).padStart(2, "0")}
              </Text>
            </View>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3 },
              ]}
            >
              Hours
            </Text>
          </View>
          <View style={{ alignItems: "center" }}>
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 6,
                backgroundColor: "rgba(220, 38, 38, 0.40)",
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_24,
                  { color: AppColors.red_FA0A0A },
                ]}
              >
                {String(timeLeft.minutes).padStart(2, "0")}
              </Text>
            </View>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3 },
              ]}
            >
              Minutes
            </Text>
          </View>
          <View style={{ alignItems: "center" }}>
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 6,
                backgroundColor: "rgba(220, 38, 38, 0.40)",
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_24,
                  { color: AppColors.red_FA0A0A },
                ]}
              >
                {String(timeLeft.seconds).padStart(2, "0")}
              </Text>
            </View>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3 },
              ]}
            >
              Second
            </Text>
          </View>
        </View>
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_16,
            { color: AppColors.white, textAlign: "center" },
          ]}
        >
          {eventDate}
        </Text>
      </LinearGradient>

      {/* Event Details Section */}
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.8)", "rgba(17, 24, 39, 0.9)"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          borderRadius: 12,
          borderWidth: 1,
          borderColor: "rgba(55, 65, 81, 0.5)",
          padding: 16,
        }}
      >
        {/* Top Section: Images */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <View style={{ alignItems: "center" }}>
            <Image
              source={
                details?.eventImageUrl
                  ? { uri: details.eventImageUrl }
                  : AppImages.KO_LOGO_Main
              }
              style={{ width: 80, height: 80, borderRadius: 8 }}
              resizeMode="contain"
            />
          </View>
          <View style={{ alignItems: "center" }}>
            <Image
              source={
                details?.gameImage
                  ? { uri: details.gameImage }
                  : AppImages.KO_LOGO_Main
              }
              style={{
                width: 80,
                height: 80,
                borderRadius: 8,
              }}
              resizeMode="cover"
            />
          </View>
        </View>

        {/* Labels Row */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3 },
              ]}
            >
              Event type
            </Text>
            <Text
              style={[AppFontStyle.BOLD_ANYBODY_16, { color: AppColors.white }]}
            >
              KALLOUT
            </Text>
          </View>
          <View style={{ flex: 1, alignItems: "center" }}>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3 },
              ]}
            >
              Game type
            </Text>
            <Text
              style={[AppFontStyle.BOLD_ANYBODY_16, { color: AppColors.white }]}
            >
              1 vs 1
            </Text>
          </View>
          <View style={{ flex: 1, alignItems: "flex-end" }}>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3, textAlign: "right" },
              ]}
            >
              Game
            </Text>
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_14,
                { color: AppColors.white, textAlign: "right" },
              ]}
            >
              {details?.gameTitle || "FIFA"}
            </Text>
          </View>
        </View>

        {/* Challenge Stakes Section */}
        <View>
          <Text
            style={[
              AppFontStyle.REGULAR_ANYBODY_14,
              { color: AppColors.gray_C3C3C3, marginBottom: 12 },
            ]}
          >
            The challenge is for {getBeltCount("bronze")} bronze
          </Text>

          {/* Belts Row */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-end",
              gap: 8,
            }}
          >
            {/* Belts */}
            {beltTypes.map(
              (type) =>
                getBeltCount(type) > 0 && (
                  <View key={type} style={{ alignItems: "center" }}>
                    <Image
                      source={getBeltIcon(type)}
                      style={{ width: 40, height: 40, marginBottom: 4 }}
                      resizeMode="contain"
                    />
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_16,
                        { color: AppColors.white },
                      ]}
                    >
                      {getBeltCount(type)}
                    </Text>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_10,
                        { color: AppColors.gray_C3C3C3 },
                      ]}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Text>
                  </View>
                )
            )}

            {/* Credits and Balance */}
            <View style={{ marginLeft: 8 }}>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { color: AppColors.gray_C3C3C3, marginBottom: 4 },
                ]}
              >
                worth {details?.totalCredits || 0} credits
              </Text>
              <View
                style={{
                  backgroundColor: "rgba(17, 24, 39, 0.8)",
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: "rgba(55, 65, 81, 0.8)",
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                }}
              >
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_20,
                    { color: AppColors.white },
                  ]}
                >
                  ${details?.totalBalance?.toFixed(0) || "120"}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </LinearGradient>
    </View>
  );
};

export default MobileGameShowCard;
